import React, { useState, useEffect, useCallback } from 'react';
import {
  getHeroContent, StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage, AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component for proper editing and saving
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  // Get the current value from storeTheme first, then from props
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  // Update local value when storeTheme changes or when editing starts
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    // Save the current local value to parent
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    // Deactivate edit mode
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      // Then navigate if provided and not in modal
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    // Save on blur (when clicking outside)
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#ff6b6b] focus:ring-2 focus:ring-[#ff6b6b]/20";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  // Display the current value - force re-render when storeTheme changes
  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function OrbitLayout({ p }) {
  const T = p.storeTheme || {};
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeModelIndex, setActiveModelIndex] = useState(0);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Force re-render when storeTheme changes
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    // This will trigger re-render when storeTheme updates
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);
  
  const products = (p.filteredProducts || []).slice(0, 8);
  
  // Hero images - DIRECTLY FROM CONFIG
  const orbitHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32e35743-1600-4df6-b1f9-2b8ef37d8b50_1.jpg",
    },
    {
      field: "orbitHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg",
    },
    {
      field: "orbitHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/76d80a10-f428-498e-a615-dd7b06cba415_3.jpg",
    },
    {
      field: "orbitHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a32c8344-da63-4169-854b-911b161c31c0_4.jpg",
    },
    {
      field: "orbitHeroImage5",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg",
    },
  ];

  // Get editable hero images
  const orbitHeroImages = orbitHeroImageConfigs.map((img) => ({
    ...img,
    src: p.storeTheme?.[img.field] || img.src,
  }));

  // Model data with editable fields and imageField
  const modelData = orbitHeroImageConfigs.map((img, idx) => ({
    id: idx + 1,
    nameKey: `model${idx + 1}_name`,
    name: `Model ${idx + 1}`,
    imageField: img.field,
    image: p.storeTheme?.[img.field] || img.src,
    subtitleKey: `model${idx + 1}_subtitle`,
    subtitle: `Aura Face ${new Date().getFullYear()}`,
    collectionKey: `model${idx + 1}_collection`,
    collection: `Collection 2024`
  }));
  
  // Category data with editable fields
  const categories = [
    { key: "category_summer_dresses", name: "Summer Dresses", icon: "👗", color: "#ff6b6b" },
    { key: "category_evening_gowns", name: "Evening Gowns", icon: "✨", color: "#4ecdc4" },
    { key: "category_accessories", name: "Accessories", icon: "👜", color: "#ffe66d" },
    { key: "category_footwear", name: "Footwear", icon: "👠", color: "#ffb347" },
  ];
  
  // Navigate to page - normal navigation
  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Don't navigate if we're actively editing
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);
  
  // Handle navigation after Enter key in edit mode
  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  // Mouse move for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 10,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  
  // Animate models sliding down
  useEffect(() => {
    const timeouts = [];
    orbitHeroImageConfigs.forEach((_, index) => {
      const timeout = setTimeout(() => {
        setActiveModelIndex(prev => prev + 1);
      }, index * 400);
      timeouts.push(timeout);
    });
    return () => timeouts.forEach(clearTimeout);
  }, [orbitHeroImageConfigs]);
  
  // Staggered animation for product cards
  useEffect(() => {
    const timers = [];
    products.forEach((_, index) => {
      const timer = setTimeout(() => {
        setVisibleCards(prev => [...prev, index]);
      }, index * 100);
      timers.push(timer);
    });
    return () => timers.forEach(clearTimeout);
  }, [products]);
  
  // Set models loaded after all models appear
  useEffect(() => {
    if (activeModelIndex >= orbitHeroImageConfigs.length) {
      setTimeout(() => setModelsLoaded(true), 500);
    }
  }, [activeModelIndex, orbitHeroImageConfigs.length]);
  
  const copy = getHeroContent(p, {
    eyebrow: "AURA FASHION",
    title: "Walk with elegance",
    subtitle: "Discover the latest trends with our stunning model collection",
    primaryCta: "Shop Now",
    secondaryCta: "View Collection",
  });

  const navLinks = [
    { key: "navHome", label: "HOME", page: "home" },
    { key: "navModels", label: "MODELS", page: "models" },
    { key: "navCollection", label: "COLLECTION", page: "collection" },
    { key: "navAbout", label: "ABOUT", page: "about" },
  ];
  
  // Get editable products with imageField
  const editableProducts = products.map((product) => ({
    ...product,
    productImage: product.productImage || product.image,
  }));
  
  return (
    <div key={refreshKey} className="min-h-screen bg-gradient-to-br from-white to-gray-50" style={{ fontFamily: T.fontFamily || "'Poppins', sans-serif" }}>
      
      {/* Top Bar */}
      <div className="bg-[#1a1a2e] text-white py-2 text-center text-sm">
        <span className="animate-pulse">✨</span> 
        <InlineEditableTextWithNav p={p} field="topBarText" value="FREE SHIPPING ON ORDERS OVER ₹4999" onNavigate={handleNavigateAfterEdit} navPage="home" />
        <span className="animate-pulse">✨</span>
      </div>
      
      {/* Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm py-3" : "bg-white/80 backdrop-blur-sm py-4"}`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border-2 border-[#ff6b6b]" />
            <span className="font-bold text-xl text-[#1a1a2e]">
              <InlineEditableTextWithNav p={p} field="businessName" value={p.storeInfo?.businessName || "Orbit Fashion"} onNavigate={handleNavigateAfterEdit} navPage="home" />
            </span>
          </div>
          
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
            <svg className="w-6 h-6 text-[#1a1a2e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex gap-8">
            {navLinks.map(item => (
              <button key={item.key} onClick={(e) => navigateTo(item.page, e)} className={`text-sm transition ${activePage === item.page ? "text-[#ff6b6b]" : "text-gray-600 hover:text-[#ff6b6b]"}`}>
                <InlineEditableTextWithNav p={p} field={item.key} value={item.label} onNavigate={handleNavigateAfterEdit} navPage={item.page} />
              </button>
            ))}
          </div>
          
          <div className="flex gap-4">
            <button onClick={p.handleGoToWishlist} className="relative">
              <FaHeart className="text-gray-600 hover:text-[#ff6b6b] transition" />
              {p.wishlistCount > 0 && (<span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#ff6b6b] text-white text-[9px] flex items-center justify-center">{p.wishlistCount}</span>)}
            </button>
            <CartBtn p={p} />
            {p.userMenuElement}
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 flex flex-col gap-2 z-50">
            {navLinks.map(item => (
              <button key={item.key} onClick={(e) => navigateTo(item.page, e)} className={`text-sm py-2 px-3 rounded-lg text-left ${activePage === item.page ? "bg-[#ff6b6b]/10 text-[#ff6b6b]" : "text-gray-700"}`}>
                <InlineEditableTextWithNav p={p} field={item.key} value={item.label} onNavigate={handleNavigateAfterEdit} navPage={item.page} />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <section className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <span className="text-[#ff6b6b] text-sm tracking-wider uppercase animate-fade-in">
              <InlineEditableTextWithNav p={p} field="heroEyebrow" value={copy.eyebrow} onNavigate={handleNavigateAfterEdit} navPage="home" />
            </span>
            <h1 className="text-4xl md:text-5xl font-light text-[#1a1a2e] mt-2 animate-slide-up">
              <InlineEditableTextWithNav p={p} field="heroTitle" value={copy.title} onNavigate={handleNavigateAfterEdit} navPage="home" />
            </h1>
            <p className="text-gray-500 mt-2 max-w-2xl mx-auto animate-slide-up delay-100">
              <InlineEditableTextWithNav p={p} field="heroSubtitle" value={copy.subtitle} onNavigate={handleNavigateAfterEdit} navPage="home" />
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
            {modelData.map((model, idx) => (
              <div key={idx} className={`relative overflow-hidden rounded-2xl shadow-lg group cursor-pointer transition-all duration-700 ease-out ${idx < activeModelIndex ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full"}`} style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.2, 0.64, 1)" }} onClick={(e) => navigateTo("collection", e)}>
                <div className="aspect-[3/4]">
                  <InlineEditableImage p={p} field={model.imageField} className="block h-full w-full overflow-hidden">
                    <img src={model.image} alt={`Model ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-sm font-semibold">
                    <InlineEditableTextWithNav p={p} field={model.nameKey} value={model.name} onNavigate={handleNavigateAfterEdit} navPage="collection" isInModal={true} />
                  </p>
                  <p className="text-xs opacity-80">
                    <InlineEditableTextWithNav p={p} field={model.collectionKey} value={model.collection} onNavigate={handleNavigateAfterEdit} navPage="collection" isInModal={true} />
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {modelsLoaded && (
            <div className="text-center animate-bounce-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff6b6b]/10 rounded-full">
                <span className="text-[#ff6b6b]">✨</span>
                <span className="text-sm text-[#1a1a2e]">
                  <InlineEditableTextWithNav p={p} field="models_arrived_message" value="All Models Have Arrived!" onNavigate={handleNavigateAfterEdit} navPage="home" />
                </span>
                <span className="text-[#ff6b6b]">✨</span>
              </div>
            </div>
          )}
        </section>
      )}
      
      {/* ==================== COLLECTION PAGE ==================== */}
      {activePage === "collection" && (
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center mb-10">
            <span className="text-[#ff6b6b] text-sm tracking-wider uppercase">
              <InlineEditableTextWithNav p={p} field="collection_badge" value="The Collection" onNavigate={handleNavigateAfterEdit} navPage="collection" />
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-[#1a1a2e] mt-2">
              <InlineEditableTextWithNav p={p} field="collectionHeading" value="Featured Pieces" onNavigate={handleNavigateAfterEdit} navPage="collection" />
            </h2>
            <div className="w-16 h-px bg-gradient-to-r from-[#ff6b6b] to-[#4ecdc4] mx-auto mt-3" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {editableProducts.map((product, idx) => {
              const entrance = { x: idx % 2 === 0 ? -100 : 100, y: 0, rotate: idx % 2 === 0 ? -10 : 10, scale: 0.8 };
              const isVisible = visibleCards.includes(idx);
              return (
                <div key={product._id} className={`group cursor-pointer transition-all duration-600 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0 scale-100" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-${entrance.rotate} scale-${entrance.scale * 100}`}`} style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.2, 0.64, 1)", transitionDelay: `${idx * 0.08}s` }} onMouseEnter={() => setHoveredCard(idx)} onMouseLeave={() => setHoveredCard(null)} onClick={() => setSelectedProduct(product)}>
                  <div className="relative overflow-hidden rounded-xl bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="relative aspect-square overflow-hidden bg-gray-100">
                      {product.productImage ? (
                        <img src={AWS_URL + product.productImage} alt={product.productName} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-gray-100 to-gray-200">{idx % 2 === 0 ? "👗" : "👠"}</div>
                      )}
                      {/* REMOVED Quick Add Button */}
                      {product.discount > 0 && (<div className="absolute top-2 left-2 bg-[#ff6b6b] text-white text-xs font-bold px-2 py-0.5 rounded-full">-{product.discount}%</div>)}
                      {idx < 2 && (<div className="absolute top-2 right-2 bg-[#4ecdc4] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        <InlineEditableTextWithNav p={p} field="new_badge" value="NEW" onNavigate={handleNavigateAfterEdit} navPage="collection" />
                      </div>)}
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium text-gray-800 text-sm truncate">
                        <InlineEditableTextWithNav p={p} field={`product_${idx}_name`} value={product.productName || "Product Name"} onNavigate={handleNavigateAfterEdit} navPage="collection" />
                      </h3>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-lg font-bold text-[#ff6b6b]">
                          ₹<InlineEditableTextWithNav p={p} field={`product_${idx}_price`} value={product.salePrice || "0"} onNavigate={handleNavigateAfterEdit} navPage="collection" />
                        </span>
                        {product.mrp > product.salePrice && (<span className="text-xs text-gray-400 line-through">₹{product.mrp}</span>)}
                      </div>
                      <div className="mt-1 flex items-center gap-0.5">
                        {[...Array(4)].map((_, i) => (<span key={i} className="text-[#ffb347] text-[10px]">★</span>))}
                        <span className="text-[10px] text-gray-400 ml-1">
                          <InlineEditableTextWithNav p={p} field="rating_text" value="(4.5)" onNavigate={handleNavigateAfterEdit} navPage="collection" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-10">
            <button onClick={() => p.setActiveTab("products")} className="group px-6 py-3 rounded-full bg-gradient-to-r from-[#ff6b6b] to-[#4ecdc4] text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg inline-flex items-center gap-2">
              <InlineEditableTextWithNav p={p} field="view_full_collection" value="View Full Collection" onNavigate={handleNavigateAfterEdit} navPage="collection" />
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>
        </section>
      )}
      
      {/* ==================== MODELS PAGE ==================== */}
      {activePage === "models" && (
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center mb-10">
            <span className="text-[#ff6b6b] text-sm tracking-wider uppercase inline-flex">
              <InlineEditableTextWithNav p={p} field="modelsSectionTitle" value="Our Models" onNavigate={handleNavigateAfterEdit} navPage="models" />
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-[#1a1a2e] mt-2">
              <InlineEditableTextWithNav p={p} field="modelsSectionHeading" value="Meet the Faces of Aura" onNavigate={handleNavigateAfterEdit} navPage="models" />
            </h2>
            <div className="w-16 h-px bg-gradient-to-r from-[#ff6b6b] to-[#4ecdc4] mx-auto mt-3" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {modelData.map((model, idx) => (
              <div key={idx} className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="h-80 overflow-hidden">
                  <InlineEditableImage p={p} field={model.imageField} className="block h-full w-full overflow-hidden">
                    <img src={model.image} alt={model.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-semibold text-[#1a1a2e]">
                    <InlineEditableTextWithNav p={p} field={model.nameKey} value={model.name} onNavigate={handleNavigateAfterEdit} navPage="models" />
                  </h3>
                  <p className="text-[#ff6b6b] text-sm mt-1">
                    <InlineEditableTextWithNav p={p} field={model.subtitleKey} value={model.subtitle} onNavigate={handleNavigateAfterEdit} navPage="models" />
                  </p>
                  <button onClick={(e) => navigateTo("collection", e)} className="mt-4 px-6 py-2 rounded-full border border-[#ff6b6b] text-[#ff6b6b] hover:bg-[#ff6b6b] hover:text-white transition">
                    <InlineEditableTextWithNav p={p} field="modelCardCta" value="View Collection" onNavigate={handleNavigateAfterEdit} navPage="collection" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="max-w-7xl mx-auto px-4 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#ff6b6b] text-sm tracking-wider uppercase inline-flex">
                <InlineEditableTextWithNav p={p} field="aboutSectionLabel" value="About Aura" onNavigate={handleNavigateAfterEdit} navPage="about" />
              </span>
              <h2 className="text-4xl md:text-5xl font-light text-[#1a1a2e] mt-3">
                <InlineEditableTextWithNav p={p} field="aboutSectionHeading" value="Where Fashion Meets Art" onNavigate={handleNavigateAfterEdit} navPage="about" />
              </h2>
              <div className="w-16 h-px bg-[#ff6b6b] mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav p={p} field="aboutSectionParagraph1" value="Aura Fashion was born from a desire to create clothing that empowers and inspires. Every piece is designed with intention, crafted with care, and made to last." onNavigate={handleNavigateAfterEdit} navPage="about" />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav p={p} field="aboutSectionParagraph2" value="We believe that fashion should be accessible, sustainable, and beautiful. Our collections are a celebration of diversity, creativity, and individual expression." onNavigate={handleNavigateAfterEdit} navPage="about" />
              </p>
              <div className="flex gap-6">
                <div>
                  <p className="text-3xl font-bold text-[#ff6b6b]">
                    <InlineEditableTextWithNav p={p} field="aboutStatCollections" value="100+" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav p={p} field="aboutLabelCollections" value="Collections" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#ff6b6b]">
                    <InlineEditableTextWithNav p={p} field="aboutStatModels" value="50+" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav p={p} field="aboutLabelModels" value="Models" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#ff6b6b]">
                    <InlineEditableTextWithNav p={p} field="aboutStatCustomers" value="10k+" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav p={p} field="aboutLabelCustomers" value="Customers" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <InlineEditableImage p={p} field="orbitAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.orbitAboutImage || "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80"} 
                  alt="About Aura" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* Category Section - Only on home */}
      {activePage === "home" && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((cat, idx) => (
              <div key={cat.key} className="text-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer group animate-fade-in" style={{ animationDelay: `${idx * 0.15}s` }} onClick={(e) => navigateTo("collection", e)}>
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform" style={{ color: cat.color }}>{cat.icon}</div>
                <p className="text-sm text-gray-700">
                  <InlineEditableTextWithNav p={p} field={cat.key} value={cat.name} onNavigate={handleNavigateAfterEdit} navPage="collection" />
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-4">
        <div className="flex justify-center gap-2 flex-wrap">
          {[["products", "COLLECTION"], ["services", "SERVICES"], ["details", "ABOUT"]].map(([key, label]) => (
            <button key={key} onClick={() => p.setActiveTab(key)} className={`px-6 py-2 rounded-full text-sm transition-all duration-300 ${p.activeTab === key ? "bg-gradient-to-r from-[#ff6b6b] to-[#4ecdc4] text-white" : "text-gray-500 hover:text-[#ff6b6b]"}`}>
              <InlineEditableTextWithNav p={p} field={`tab_${key}_label`} value={label} onNavigate={handleNavigateAfterEdit} navPage={activePage} />
            </button>
          ))}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-6"><TabContent p={p} /></div>
      
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-[#1a1a2e] to-[#2a2a4e] py-12 mt-8">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-[#ff6b6b] text-xs tracking-[0.2em] uppercase">
            <InlineEditableTextWithNav p={p} field="newsletter_badge" value="Subscribe" onNavigate={handleNavigateAfterEdit} navPage="home" />
          </span>
          <h3 className="text-2xl font-light text-white mt-2">
            <InlineEditableTextWithNav p={p} field="newsletter_title" value="Join the Aura Community" onNavigate={handleNavigateAfterEdit} navPage="home" />
          </h3>
          <p className="text-gray-300 text-sm mt-2">
            <InlineEditableTextWithNav p={p} field="newsletter_subtitle" value="Get exclusive access to new collections and offers" onNavigate={handleNavigateAfterEdit} navPage="home" />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input type="email" placeholder={p.storefrontText?.email_placeholder || "Your email address"} className="flex-1 px-5 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/30 focus:border-[#ff6b6b] outline-none transition" />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-[#ff6b6b] text-white font-semibold hover:bg-white hover:text-[#1a1a2e] transition"
            >
              <InlineEditableTextWithNav p={p} field="subscribe_button" value="Subscribe" onNavigate={handleNavigateAfterEdit} navPage="home" />
            </button>
          </div>
        </div>
      </div>
      
      <StoreFooter p={p} />
      
      {/* Product Modal - REMOVED Add to Cart button */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelectedProduct(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="relative h-72">
              {selectedProduct.productImage ? <img src={AWS_URL + selectedProduct.productImage} alt={selectedProduct.productName} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center text-6xl">👗</div>}
              <button onClick={() => setSelectedProduct(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/30 text-white hover:bg-black/50 transition">✕</button>
            </div>
            <div className="p-5">
              <h3 className="text-xl font-semibold text-gray-800">
                <InlineEditableTextWithNav p={p} field={`product_modal_name`} value={selectedProduct.productName || "Product Name"} onNavigate={handleNavigateAfterEdit} navPage="collection" isInModal={true} />
              </h3>
              <p className="text-[#ff6b6b] text-2xl font-bold mt-1">
                ₹<InlineEditableTextWithNav p={p} field={`product_modal_price`} value={selectedProduct.salePrice || "0"} onNavigate={handleNavigateAfterEdit} navPage="collection" isInModal={true} />
              </p>
              <div className="mt-4 text-sm text-gray-600 text-center bg-gray-50 p-3 rounded-full border border-gray-200">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="modal_decorative_message" 
                  value="✨ A stunning piece from our collection ✨"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="collection"
                  isInModal={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes slide-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.6s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.8); } 50% { transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        .delay-100 { animation-delay: 0.1s; animation-fill-mode: forwards; }
      `}</style>
    </div>
  );
}